import * as React from 'react';
import { withAuthContext } from 'containers/AccessControl/WithAuthContext';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { IAppointmentState, IAppointmentWithCustomer } from 'models/shop/appointments';
import moment = require('moment');
import { Appointment } from 'components/Common/Appointment';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import { InfiniteScrollIntersection } from 'components/Common/InfiniteScrollIntersection';
require('./style.scss');

interface IProps {
	appointments: IAppointmentState;
	isShopAppointment?: boolean;
	isShopCustomerAppointment?: boolean;
	t?: (key: string) => string;
	loadMoreData?: () => void;
}

interface IState {
	incomingAppointments: IAppointmentWithCustomer[];
	pastAppointments: IAppointmentWithCustomer[];
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class Appointments extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			incomingAppointments: [],
			pastAppointments: [],
		};
	}

	componentDidUpdate(prevProps: Readonly<IProps>): void {
		const { appointments, isShopAppointment, isShopCustomerAppointment } = this.props;

		if (JSON.stringify(prevProps?.appointments?.items) !== JSON.stringify(appointments?.items)) {
			const incomingAppointments: IAppointmentWithCustomer[] = [];
			const pastAppointments: IAppointmentWithCustomer[] = [];
			let groupedAppointments = {};

			// For user appointments view and single customers page, group appointments by orderId inside appointment.orderItem, do not reduce recurring event appointments (reduced separately below)
			if (
				(!isShopAppointment && appointments.items?.length) ||
				(isShopCustomerAppointment && appointments.items?.length)
			) {
				groupedAppointments = appointments.items.reduce((result, appointment) => {
					const orderId = appointment.orderItem.orderId;

					if (!result[orderId] && !appointment?.orderItem?.recurringBookingId) {
						result[orderId] = {
							shopOrder: appointment.shopOrder,
							shipping: appointment.shipping,
							orderItem: [],
							order: appointment.order,
						};
					}

					if (!appointment?.orderItem?.recurringBookingId) {
						result[orderId].orderItem.push(appointment.orderItem);
					}

					return result;
				}, {});
			}

			if (appointments?.items?.length) {
				// For shop appointments go through all items ungrouped (group for shop customers)
				if (isShopAppointment && !isShopCustomerAppointment) {
					appointments.items.forEach((x) => {
						if (x?.orderItem?.bookedOrderItemDatas?.length) {
							const bookedOrderItemData = x.orderItem.bookedOrderItemDatas[0];
							const timezone = x.orderItem.officeZoneInfo;
							// if future appointments
							if (momentWithTimezone(timezone, bookedOrderItemData.startTime).isAfter(moment(new Date()))) {
								incomingAppointments.unshift(x);
							} else {
								// else past appointments
								pastAppointments.push(x);
							}
						}
					});
				} else {
					// For user/customer appointments go through grouped appointments

					const groupedAppointmentsArray: IAppointmentWithCustomer[] = Object.values(groupedAppointments);
					const recurringAppointments =
						appointments?.items?.filter((item) => item?.orderItem?.recurringBookingId) || [];

					// Add grouped recurring appointments to groupedAppointmentsArray
					if (recurringAppointments?.length) {
						const timezone = recurringAppointments?.[0]?.orderItem?.officeZoneInfo;
						const groupedRecurringAppointmentsObject = recurringAppointments.reduce((result, appointment) => {
							// Group appointments by day
							// Edge case: same appointment items can technically go into new day which will cause them to display in a new card
							// TODO: find if there is a more efficient way to group to avoid above edge case
							const appointmentStartOfDay = momentWithTimezone(timezone, appointment.orderItem.productSchedule)
								.startOf('day')
								.toISOString();

							if (!result[appointmentStartOfDay]) {
								result[appointmentStartOfDay] = {
									shopOrder: appointment.shopOrder,
									shipping: appointment.shipping,
									orderItem: [],
									order: appointment.order,
								};
							}

							result[appointmentStartOfDay].orderItem.push(appointment.orderItem);

							return result;
						}, {});

						const groupedRecurringAppointmentsArray: IAppointmentWithCustomer[] = Object.values(
							groupedRecurringAppointmentsObject,
						);

						// When clicking on recurring appointment card go to same order as the entire recurring event for now
						groupedAppointmentsArray.push(...groupedRecurringAppointmentsArray);
					}

					// Sort by date descending as recurrings are at the end of the array
					groupedAppointmentsArray.sort((a, b) =>
						moment(b.orderItem[0].productSchedule).diff(moment(a.orderItem[0].productSchedule)),
					);

					groupedAppointmentsArray.forEach((x) => {
						if (x?.orderItem?.[0]?.bookedOrderItemDatas?.length) {
							const bookedOrderItemData = x.orderItem[0].bookedOrderItemDatas[0];
							const timezone = x.orderItem[0].officeZoneInfo;

							// if future appointments
							if (momentWithTimezone(timezone, bookedOrderItemData.startTime).isAfter(moment(new Date()))) {
								incomingAppointments.unshift(x);
							} else {
								// else past appointments
								pastAppointments.push(x);
							}
						}
					});
				}
			}

			this.setState({ incomingAppointments, pastAppointments });
		}
	}

	render() {
		const { t, isShopAppointment, appointments, isShopCustomerAppointment } = this.props;
		const emptyResults: JSX.Element = (
			<div className="noAppointmentsContainer">
				<img src={require('containers/Html/images/no-results-logo.png')} alt="no results image" />
				<div className="textContainer">
					<p className="p18">{t('User_UserCard_NothingHereYet')}</p>
					<p className="p14">{t('User_UserCard_UserHasNoAppointments')}</p>
				</div>
			</div>
		);

		const renderAppointments = (type: 'incoming' | 'past', arrOfItems: IAppointmentWithCustomer[]): JSX.Element[] => {
			return arrOfItems.map((x, index) => (
				<Appointment
					appointment={x}
					key={index}
					isShopAppointment={isShopAppointment}
					isShopCustomerAppointment={isShopCustomerAppointment}
					type={type}
					customClassName={isShopAppointment ? '' : 'userAppointment'}
					showCancelButton={true}
				/>
			));
		};

		return (
			<>
				<div className={`row ${!isShopCustomerAppointment ? 'justify-content-center' : ''}`}>
					<div className={!isShopCustomerAppointment ? 'col-lg-8 col-xl-7' : 'col-12'}>
						<h1 className="appointments-mainTitle">{t('User_UserCard_Appointments')}</h1>
					</div>
				</div>

				<InfiniteScrollIntersection
					dataLength={appointments?.items?.length}
					next={this.props.loadMoreData}
					hasMore={this.props?.appointments?.itemsPage?.totalPages > this.props?.appointments?.itemsPage?.currentPage}
				>
					{!!this.state.incomingAppointments.length && (
						<div
							className="row justify-content-center incomingAppointments"
							style={{ flexDirection: isShopAppointment ? 'column' : 'row' }}
						>
							<div className={!isShopCustomerAppointment ? 'col-lg-8 col-xl-7' : 'col-12'}>
								<h2 className="appointments-title">{t('User_UserCard_IncomingAppointments')}</h2>
							</div>

							<div className={!isShopCustomerAppointment ? 'col-lg-8 col-xl-7' : 'col-12'}>
								{renderAppointments('incoming', this.state.incomingAppointments)}
							</div>
						</div>
					)}
					{!(this.state.incomingAppointments.length && !this.state.pastAppointments.length) && (
						<div
							className="row justify-content-center pastAppointments"
							style={{ flexDirection: isShopAppointment ? 'column' : 'row' }}
						>
							<div className={!isShopCustomerAppointment ? 'col-lg-8 col-xl-7' : 'col-12'}>
								<h2 className="appointments-title">{t('User_UserCard_PastAppointments')}</h2>
							</div>

							<div className={!isShopCustomerAppointment ? 'col-lg-8 col-xl-7' : 'col-12'}>
								{!this.state.incomingAppointments.length && !this.state.pastAppointments.length
									? emptyResults
									: renderAppointments('past', this.state.pastAppointments)}
							</div>
						</div>
					)}
				</InfiniteScrollIntersection>
			</>
		);
	}
}

export default withAuthContext(Appointments);
